import { icon } from "@fortawesome/fontawesome-svg-core";
import { WarningIcon } from "../page/icons";

function escapeHtml(value) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttribute(value) {
  return escapeHtml(value).replace(/"/g, "&quot;");
}

function parseInline(markdown) {
  let html = escapeHtml(markdown);

  html = html.replace(
    /`([^`]+)`/g,
    (_, value) => `<code>${value}</code>`
  );
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (_, text, href) =>
      `<a href="${escapeAttribute(href)}">${escapeHtml(text)}</a>`
  );
  html = html.replace(
    /\*\*([^*]+)\*\*/g,
    (_, value) => `<strong>${value}</strong>`
  );
  html = html.replace(/\*([^*]+)\*/g, (_, value) => `<em>${value}</em>`);

  return html;
}

function getLeadingSpaces(line) {
  const match = line.match(/^ */);
  return match ? match[0].length : 0;
}

function isHeading(line) {
  return /^#{1,6}\s+/.test(line);
}

function isTableStart(lines, index) {
  if (index + 1 >= lines.length) {
    return false;
  }

  return (
    /^\|.*\|$/.test(lines[index]) &&
    /^\|(?:\s*:?-+:?\s*\|)+$/.test(lines[index + 1])
  );
}

function isImageHtml(line) {
  return /^<img\s/i.test(line.trim());
}

function isListLine(line) {
  return /^(\s*)(- |\d+\. )/.test(line);
}

function isBlockquoteLine(line) {
  return /^\s*>\s?/.test(line);
}

function isAdministratorSectionStart(line) {
  return /^<!--\s*administrator:start\s*-->$/i.test(line.trim());
}

function isAdministratorSectionEnd(line) {
  return /^<!--\s*administrator:end\s*-->$/i.test(line.trim());
}

function isBlockStart(lines, index) {
  const line = lines[index];
  if (!line || !line.trim()) {
    return false;
  }

  return (
    isHeading(line) ||
    isTableStart(lines, index) ||
    isImageHtml(line) ||
    isListLine(line) ||
    isBlockquoteLine(line) ||
    isAdministratorSectionStart(line)
  );
}

function parseImage(line, resolveAssetUrl) {
  const src = line.match(/src="([^"]+)"/i)?.[1] || "";
  const alt = line.match(/alt="([^"]*)"/i)?.[1] || "";
  const width = line.match(/width="([^"]+)"/i)?.[1];
  const resolvedSrc = resolveAssetUrl(src);
  const widthAttribute = width ? ` width="${escapeAttribute(width)}"` : "";

  return `<img class="img-fluid rounded border my-4 d-block" src="${escapeAttribute(
    resolvedSrc
  )}" alt="${escapeAttribute(alt)}"${widthAttribute} />`;
}

function parseTable(lines, startIndex) {
  const rows = [];
  let index = startIndex;

  while (index < lines.length && /^\|.*\|$/.test(lines[index])) {
    rows.push(
      lines[index]
        .trim()
        .slice(1, -1)
        .split("|")
        .map((cell) => cell.trim())
    );
    index += 1;
  }

  const [headerRow, , ...bodyRows] = rows;
  const head = headerRow
    .map((cell) => `<th>${parseInline(cell)}</th>`)
    .join("");
  const body = bodyRows
    .map(
      (row) =>
        `<tr>${row
          .map((cell) => `<td>${parseInline(cell)}</td>`)
          .join("")}</tr>`
    )
    .join("");

  return {
    html: `<div class="table-responsive my-4"><table class="table table-bordered table-striped align-middle mb-0"><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></div>`,
    nextIndex: index,
  };
}

function parseList(lines, startIndex) {
  const firstMatch = lines[startIndex].match(/^(\s*)(- |\d+\. )/);
  const baseIndent = firstMatch[1].length;
  const isOrdered = /\d+\.\s/.test(firstMatch[2]);
  const tag = isOrdered ? "ol" : "ul";
  const items = [];
  let index = startIndex;

  while (index < lines.length) {
    const match = lines[index].match(/^(\s*)(- |\d+\. )(.*)$/);
    if (!match) {
      break;
    }

    const currentIndent = match[1].length;
    const currentIsOrdered = /\d+\.\s/.test(match[2]);

    if (currentIndent !== baseIndent || currentIsOrdered !== isOrdered) {
      break;
    }

    const itemLines = [match[3]];
    index += 1;

    while (index < lines.length) {
      const nextLine = lines[index];
      if (!nextLine.trim()) {
        itemLines.push("");
        index += 1;
        continue;
      }

      const nextIndent = getLeadingSpaces(nextLine);
      const nextListMatch = nextLine.match(/^(\s*)(- |\d+\. )/);

      if (nextListMatch && nextIndent === baseIndent) {
        break;
      }

      if (nextIndent <= baseIndent && !nextListMatch) {
        break;
      }

      const deindentedLine =
        nextIndent > baseIndent
          ? nextLine.slice(Math.min(baseIndent + 2, nextIndent))
          : nextLine.trimStart();
      itemLines.push(deindentedLine);
      index += 1;
    }

    items.push(`<li>${parseBlocks(itemLines).html}</li>`);
  }

  return {
    html: `<${tag} class="mb-3">${items.join("")}</${tag}>`,
    nextIndex: index,
  };
}

function parseParagraph(lines, startIndex) {
  const content = [];
  let index = startIndex;

  while (index < lines.length) {
    if (!lines[index].trim()) {
      break;
    }

    if (content.length > 0 && isBlockStart(lines, index)) {
      break;
    }

    content.push(lines[index].trim());
    index += 1;
  }

  return {
    html: `<p class="mb-3">${parseInline(content.join(" "))}</p>`,
    nextIndex: index,
  };
}

function renderInlineParagraph(lines) {
  const content = lines.map((line) => line.trim()).filter(Boolean).join(" ");
  return content ? parseInline(content) : "";
}

function getAlertClass(label) {
  const alertClassMap = {
    important: "alert-warning",
    warning: "alert-warning",
    note: "alert-info",
    info: "alert-info",
    success: "alert-success",
    danger: "alert-danger",
  };

  return alertClassMap[label.toLowerCase()] || null;
}

function getAlertIconHtml(label) {
  const normalizedLabel = label.toLowerCase();

  if (normalizedLabel === "important" || normalizedLabel === "warning") {
    return icon(WarningIcon, {
      classes: ["me-2"],
    }).html.join("");
  }

  return "";
}

function parseBlockquote(lines, startIndex, options = {}) {
  const quoteLines = [];
  let index = startIndex;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      quoteLines.push("");
      index += 1;
      continue;
    }

    if (!isBlockquoteLine(line)) {
      break;
    }

    quoteLines.push(line.replace(/^\s*>\s?/, ""));
    index += 1;
  }

  const firstContentLine = quoteLines.find((line) => line.trim());
  const alertMatch =
    firstContentLine?.match(/^\[!(.+?)\]\s*$/) ||
    firstContentLine?.match(/^\*\*(.+?)\*\*:?\s*$/);
  const alertClass = alertMatch
    ? getAlertClass(alertMatch[1].trim())
    : null;

  if (alertClass) {
    const title = alertMatch[1].trim();
    const bodyLines = quoteLines.slice(1);
    const nonEmptyBodyLines = bodyLines.filter((line) => line.trim());
    const bodyHtml =
      nonEmptyBodyLines.length === 1
        ? renderInlineParagraph(nonEmptyBodyLines)
        : parseBlocks(bodyLines, options).html;
    const alertIconHtml = getAlertIconHtml(title);

    return {
      html: `<div class="alert ${alertClass} my-4" role="alert"><div class="d-flex align-items-start"><div class="me-2">${alertIconHtml}</div><div class="flex-grow-1">${bodyHtml}</div></div></div>`,
      nextIndex: index,
    };
  }

  return {
    html: `<blockquote class="border-start border-4 ps-3 my-4">${parseBlocks(
      quoteLines,
      options
    ).html}</blockquote>`,
    nextIndex: index,
  };
}

function parseAdministratorSection(lines, startIndex, options = {}) {
  const sectionLines = [];
  let index = startIndex + 1;

  while (
    index < lines.length &&
    !isAdministratorSectionEnd(lines[index])
  ) {
    sectionLines.push(lines[index]);
    index += 1;
  }

  const nextIndex =
    index < lines.length && isAdministratorSectionEnd(lines[index])
      ? index + 1
      : index;
  const sectionHtml = parseBlocks(sectionLines, options).html;

  return {
    html: `<section class="border border-info rounded bg-info bg-opacity-10 p-4 my-4"><div class="d-flex flex-wrap align-items-center gap-2 mb-3"><span class="badge bg-info text-dark">Administrator only</span></div>${sectionHtml}</section>`,
    nextIndex,
  };
}

function parseBlocks(lines, options = {}) {
  const resolveAssetUrl = options.resolveAssetUrl || ((value) => value);
  const htmlParts = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (isHeading(line)) {
      const [, hashes, text] = line.match(/^(#{1,6})\s+(.*)$/);
      const level = hashes.length;
      const headingClasses =
        {
          1: "h2 fw-bold text-dark mb-3",
          2: "h3 fw-bold text-dark border-top pt-4 mt-4 mb-3",
          3: "h5 fw-bold text-dark mt-4 mb-3",
          4: "h5 fw-semibold text-dark mt-3 mb-2",
          5: "h6 fw-semibold text-dark mt-3 mb-2",
          6: "h6 fw-semibold text-dark mt-3 mb-2",
        }[level] || "fw-bold text-dark mb-3";
      htmlParts.push(
        `<h${level} class="${headingClasses}">${parseInline(
          text.trim()
        )}</h${level}>`
      );
      index += 1;
      continue;
    }

    if (isAdministratorSectionStart(line)) {
      const administratorSection = parseAdministratorSection(
        lines,
        index,
        options
      );
      htmlParts.push(administratorSection.html);
      index = administratorSection.nextIndex;
      continue;
    }

    if (isImageHtml(line)) {
      htmlParts.push(parseImage(line, resolveAssetUrl));
      index += 1;
      continue;
    }

    if (isTableStart(lines, index)) {
      const table = parseTable(lines, index);
      htmlParts.push(table.html);
      index = table.nextIndex;
      continue;
    }

    if (isListLine(line)) {
      const list = parseList(lines, index);
      htmlParts.push(list.html);
      index = list.nextIndex;
      continue;
    }

    if (isBlockquoteLine(line)) {
      const blockquote = parseBlockquote(lines, index, options);
      htmlParts.push(blockquote.html);
      index = blockquote.nextIndex;
      continue;
    }

    const paragraph = parseParagraph(lines, index);
    htmlParts.push(paragraph.html);
    index = paragraph.nextIndex;
  }

  return { html: htmlParts.join("") };
}

function filterMarkdownByPerspective(markdown, options = {}) {
  const includeAdministratorSections =
    options.documentationPerspective === "administrator";
  const administratorSectionPattern =
    /<!--\s*administrator:start\s*-->[\s\S]*?<!--\s*administrator:end\s*-->/gi;

  const filteredMarkdown = includeAdministratorSections
    ? markdown
    : markdown.replace(administratorSectionPattern, "");

  return filteredMarkdown;
}

function renderMarkdownToHtml(markdown, options = {}) {
  const normalizedMarkdown = filterMarkdownByPerspective(
    markdown,
    options
  ).replace(/\r\n/g, "\n");
  return parseBlocks(normalizedMarkdown.split("\n"), options).html;
}

export { renderMarkdownToHtml };
